import { matchPath } from "react-router-dom";

/**
 * Absolute path patterns shared by `SchemaRouter` and the page-name resolver, so
 * the analytics page-name table cannot drift from the routes the router renders.
 */
export const PATHS = {
    LANDING: "/",
    OAUTH: "/oauth/:action",
    HOME: "/home",
    SEARCH: "/search",
    ORGANIZATION: "/organization/:organizationId",
    CREATE_GROUP: "/organization/:organizationId/create-group",
    GROUP: "/organization/:organizationId/group/:groupId",
    GROUP_MEMBERS: "/organization/:organizationId/group/:groupId/members",
    LIBRARY: "/organization/:organizationId/group/:groupId/library/:libraryId",
    LIBRARY_MEMBERS: "/organization/:organizationId/group/:groupId/library/:libraryId/members",
    SCHEMA_IN_LIBRARY: "/organization/:organizationId/group/:groupId/library/:libraryId/schema/:schemaId",
    EDITOR: "/organization/:organizationId/group/:groupId/library/:libraryId/edit",
    EDITOR_SCHEMA: "/organization/:organizationId/group/:groupId/library/:libraryId/edit/:schemaId",
    SCHEMA: "/schema/:schemaId",
} as const;

/** Returns `child` relative to `parent`, for routes nested under a parent route. */
export function relativeTo(parent: string, child: string): string {
    const prefix = `${parent}/`;
    if (!child.startsWith(prefix)) {
        throw new Error(`Route "${child}" is not nested under "${parent}"`);
    }
    return child.slice(prefix.length);
}

// First match wins. Every entry is matched exactly, so order only matters for readability.
const PAGE_TABLE: ReadonlyArray<{ pattern: string; name: string }> = [
    { pattern: PATHS.LANDING, name: "landing" },
    { pattern: PATHS.HOME, name: "home" },
    { pattern: PATHS.SEARCH, name: "search" },
    { pattern: PATHS.ORGANIZATION, name: "organization" },
    { pattern: PATHS.CREATE_GROUP, name: "create-group" },
    { pattern: PATHS.GROUP_MEMBERS, name: "members" },
    { pattern: PATHS.LIBRARY_MEMBERS, name: "members" },
    { pattern: PATHS.EDITOR, name: "editor" },
    { pattern: PATHS.EDITOR_SCHEMA, name: "editor" },
    { pattern: PATHS.SCHEMA_IN_LIBRARY, name: "schema-in-library" },
    { pattern: PATHS.LIBRARY, name: "library" },
    { pattern: PATHS.GROUP, name: "group" },
    { pattern: PATHS.SCHEMA, name: "schema" },
];

export interface ResolvedPage {
    name: string;
    /** The matching route pattern, or undefined for `not-found`. Never contains real ids. */
    pattern: string | undefined;
}

export function resolvePage(pathname: string): ResolvedPage {
    for (const entry of PAGE_TABLE) {
        if (matchPath({ path: entry.pattern, end: true }, pathname)) {
            return { name: entry.name, pattern: entry.pattern };
        }
    }
    return { name: "not-found", pattern: undefined };
}

export function isOAuthPath(pathname: string): boolean {
    return matchPath({ path: PATHS.OAUTH, end: true }, pathname) !== null;
}
